import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, ArrowRight, X } from "lucide-react";

// Shared by every public "ask the AI" teaser (the homepage hero prompt box, the footer chat
// teaser): submitting a prompt as an anonymous visitor always lands on the SAME lightweight
// invite — never a bespoke auth form per widget — and the CTA always points at the site's real
// signup page (`registrationPath`, resolved server-side by `PublicNavigationHelper#public_signup_path`).
export function RegistrationModal({
  isOpen,
  onClose,
  registrationPath,
}: {
  isOpen: boolean;
  onClose: () => void;
  registrationPath: string;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;

  const modal = (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        className={`absolute inset-0 bg-slate-800/70 transition-opacity duration-300 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ${
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative">
          <div className="p-6 text-center">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-default-100 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-default-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Accédez à votre assistant IA
            </h2>
            <p className="text-gray-600 mb-6">
              Créez votre compte gratuitement pour bénéficier de
              l'accompagnement personnalisé d'inSiti dans votre projet de
              commerce.
            </p>
            <div className="space-y-3">
              <a
                href={registrationPath}
                className="inline-flex items-center justify-center gap-x-2 w-full px-4 py-3 text-base font-medium rounded-md bg-default-600 text-white hover:bg-default-600/90 transition-colors"
              >
                <span>Créer mon compte</span>
                <ArrowRight className="size-5" />
              </a>
              <button
                type="button"
                className="w-full text-sm text-gray-500 hover:text-gray-700"
                onClick={onClose}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
